import React, { useState } from 'react';
import { FormTemplate } from '../../types/formBuilder';
import { useLanguage } from '../../contexts/LanguageContext';
import FieldRenderer from './FieldRenderer';
import SectionRenderer from './SectionRenderer';

interface FormPreviewProps {
  template: FormTemplate;
}

const FormPreview: React.FC<FormPreviewProps> = ({ template }) => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState<Record<string, any>>({});

  // Group fields by section
  const fieldsBySection = template.fields.reduce((acc, field) => {
    const sectionId = field.section || 'default';
    if (!acc[sectionId]) {
      acc[sectionId] = [];
    }
    acc[sectionId].push(field);
    return acc;
  }, {} as Record<string, typeof template.fields>);

  // Sort sections by order
  const sortedSections = [...template.sections].sort((a, b) => (a.order || 0) - (b.order || 0));

  const handleFieldChange = (fieldName: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Form submitted:', formData);
    // Here you would typically send the data to your backend
  };

  return (
    <div className="h-full overflow-y-auto bg-gray-50 dark:bg-gray-900">
      <div className="max-w-4xl mx-auto p-6">
        {/* Form Header */}
        <div className="mb-8 text-center">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-4">
            {template.settings.title || template.name || t('formBuilder.untitledForm')}
          </h1>
          {template.settings.description && (
            <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
              {template.settings.description}
            </p>
          )}
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="crm-card">
          <div className="p-8">
            {/* Progress Bar */}
            {template.settings.showProgressBar && template.settings.multiStep && (
              <div className="mb-8">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Progress
                  </span>
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    Step 1 of {template.sections.length || 1}
                  </span>
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                  <div 
                    className="bg-primary-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${100 / (template.sections.length || 1)}%` }}
                  />
                </div>
              </div>
            )}

            {/* Default Section (fields without section) */}
            {fieldsBySection.default && fieldsBySection.default.length > 0 && (
              <div className="mb-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {fieldsBySection.default
                    .sort((a, b) => (a.order || 0) - (b.order || 0))
                    .map(field => (
                      <PreviewField
                        key={field.id}
                        field={field}
                        value={formData[field.name]}
                        onChange={(value) => handleFieldChange(field.name, value)}
                      />
                    ))}
                </div>
              </div>
            )}

            {/* Sections */}
            {sortedSections.map(section => (
              <SectionRenderer
                key={section.id}
                section={section}
                fields={fieldsBySection[section.id] || []}
                selectedField=""
                onSelectField={() => {}}
                onUpdateField={() => {}}
                onDeleteField={() => {}}
                isPreview={true}
              />
            ))}

            {/* Form Actions */}
            <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-600">
              <div className="flex justify-end space-x-3">
                {template.settings.allowDrafts && (
                  <button
                    type="button"
                    className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                  >
                    Save as Draft
                  </button>
                )}
                
                <button
                  type="button"
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                >
                  {template.settings.cancelButtonText || 'Cancel'}
                </button>
                
                <button
                  type="submit"
                  className="px-4 py-2 bg-primary-600 border border-transparent rounded-md text-sm font-medium text-white hover:bg-primary-700 transition-colors"
                >
                  {template.settings.submitButtonText || 'Submit'}
                </button>
              </div>
            </div>
          </div>
        </form>

        {/* Debug Info */}
        {process.env.NODE_ENV === 'development' && (
          <div className="mt-8 p-4 bg-gray-100 dark:bg-gray-800 rounded-lg">
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Form Data (Debug)
            </h3>
            <pre className="text-xs text-gray-600 dark:text-gray-400 overflow-auto">
              {JSON.stringify(formData, null, 2)}
            </pre>
          </div>
        )}
      </div>
    </div>
  );
};

// Preview Field Component with actual functionality
const PreviewField: React.FC<{
  field: any;
  value: any;
  onChange: (value: any) => void;
}> = ({ field, value, onChange }) => {
  return (
    <FieldRenderer
      field={field}
      isSelected={false}
      onSelect={() => {}}
      onUpdate={() => {}}
      onDelete={() => {}}
      isPreview={true}
    />
  );
};

export default FormPreview;
